import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Drawer } from "antd";
import Button from "@dtci/spellbook/Button";
import { useWindowsActions } from "tap/store/windows/useWindows.js";
import { tapApi } from "tap/api.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { WindowSequenceForm } from "../window-sequence-form";
import { formatFormDataForSave, validateForm } from "../utils/formUtils";

export const CreateWindowSequenceDrawer = () => {
  const { isCreateDrawerOpen, windowSequenceForm } = useSelector(
    (state) => state.windows,
  );
  const { updateWindows, clearWindowForm } = useWindowsActions();
  const { showSuccessToast, showErrorToast } = useToasts();
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const [validationErrors, setValidationErrors] = useState({});
  const [saveDisabled, setSaveDisabled] = useState(false);

  const handleClose = () => {
    setValidationErrors({});
    updateWindows({
      isCreateDrawerOpen: false,
    });
  };

  const handleAfterOpenChange = (open) => {
    if (!open) {
      clearWindowForm();
    }
  };

  const handleSave = async () => {
    const formData = windowSequenceForm;

    const errors = validateForm(formData, "create");
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showErrorToast("Please fix validation errors before saving");
      return;
    }

    setLoadingTrue();

    try {
      const payload = formatFormDataForSave(formData, null, true);
      const result = await tapApi.createWindowSequence(payload);

      // Only treat as failure when there are real error messages; an empty
      // `errors` object from the API is still truthy and must not block close.
      const hasApiFieldErrors =
        result?.errors?.windowSequenceName || result?.errors?.windowsError;
      if (result?.error || hasApiFieldErrors) {
        // Map API field errors to form validation errors
        const newErrors = {};
        if (result?.errors?.windowSequenceName) {
          newErrors.windowSequenceName = result.errors.windowSequenceName;
        }
        if (result?.errors?.windowsError) {
          newErrors.windows = result.errors.windowsError;
        }
        if (Object.keys(newErrors).length > 0) {
          setValidationErrors(newErrors);
        }
        const errorMsg =
          result?.errors?.windowSequenceName ||
          result?.errors?.windowsError ||
          result?.error?.message ||
          "Failed to create window sequence";
        showErrorToast(errorMsg);
      } else {
        showSuccessToast("Window sequence created successfully");
        setValidationErrors({});
        updateWindows({
          refreshWindows: true,
        });
        handleClose();
      }
    } catch (error) {
      console.error("Error creating window sequence:", error);
      showErrorToast("An error occurred while creating the window sequence");
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <Drawer
      className="create-drawer windows-create-drawer"
      title="Create Window Sequence"
      width={900}
      placement="right"
      open={isCreateDrawerOpen}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      destroyOnClose
      bodyStyle={{ padding: 0, backgroundColor: "#f5f5f5" }}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            iconType="fa-solid fa-xmark"
            styleType="link"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            iconType="fa-solid fa-save"
            styleType="link"
            onClick={handleSave}
            disabled={saveDisabled}
          >
            Save
          </Button>
        </div>
      }
    >
      <WindowSequenceForm
        mode="create"
        validationErrors={validationErrors}
        onNextWindowIssueChange={setSaveDisabled}
      />
    </Drawer>
  );
};
